/* ==========================================================================
   HUTCH — theme.js
   Vanilla, no dependencies. Drawers, predictive search, AJAX cart,
   variant picker, transparent header, recommendations.
   ========================================================================== */
;(function () {
  'use strict'

  var theme = window.theme || { routes: {}, strings: {} }

  /* Utilities
     ======================================================================= */
  function formatMoney (cents) {
    var format = theme.moneyFormat || '£{{amount}}'
    var value = (cents / 100).toFixed(2)
    if (/amount_no_decimals/.test(format)) value = Math.round(cents / 100).toString()
    if (/amount_with_comma_separator/.test(format)) value = value.replace('.', ',')
    value = value.replace(/[.,]00$/, '')
    return format.replace(/\{\{\s*\w+\s*\}\}/, value)
  }

  function debounce (fn, wait) {
    var t
    return function () {
      var args = arguments
      clearTimeout(t)
      t = setTimeout(function () { fn.apply(null, args) }, wait)
    }
  }

  function escapeHtml (str) {
    var div = document.createElement('div')
    div.textContent = str == null ? '' : String(str)
    return div.innerHTML
  }

  /* Drawers
     ======================================================================= */
  var overlay = document.querySelector('[data-overlay]')
  var openDrawer = null

  function getDrawer (name) {
    return document.querySelector('[data-drawer="' + name + '"]')
  }

  function open (name) {
    var el = getDrawer(name)
    if (!el) return
    if (openDrawer && openDrawer !== el) close()
    el.classList.add('is-open')
    el.setAttribute('aria-hidden', 'false')
    document.body.classList.add('drawer-open')
    if (overlay) {
      overlay.hidden = false
      requestAnimationFrame(function () { overlay.classList.add('is-visible') })
    }
    openDrawer = el
    var focusable = el.querySelector('input, button, a')
    if (focusable) focusable.focus({ preventScroll: true })
  }

  function close () {
    if (!openDrawer) return
    openDrawer.classList.remove('is-open')
    openDrawer.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('drawer-open')
    if (overlay) {
      overlay.classList.remove('is-visible')
      setTimeout(function () { if (!openDrawer) overlay.hidden = true }, 300)
    }
    openDrawer = null
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]')
    if (opener) {
      e.preventDefault()
      open(opener.getAttribute('data-drawer-open'))
      return
    }
    if (e.target.closest('[data-drawer-close]')) {
      e.preventDefault()
      close()
      return
    }
    var cartToggle = e.target.closest('[data-cart-toggle]')
    if (cartToggle && theme.cartType === 'drawer') {
      e.preventDefault()
      open('cart')
    }
  })

  if (overlay) overlay.addEventListener('click', close)
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close()
  })

  /* Announcement show/hide
     ======================================================================= */
  var announce = document.querySelector('[data-announce]')
  if (announce) {
    var announceKey = 'hutchAnnounceHidden'
    var canStore = true
    try { sessionStorage.getItem(announceKey) } catch (e) { canStore = false }
    if (!canStore || !sessionStorage.getItem(announceKey)) announce.hidden = false
    var hideBtn = announce.querySelector('[data-announce-hide]')
    if (hideBtn) {
      hideBtn.addEventListener('click', function (e) {
        e.preventDefault()
        e.stopPropagation()
        announce.hidden = true
        if (canStore) sessionStorage.setItem(announceKey, '1')
      })
    }
  }

  /* Transparent header on homepage hero
     ======================================================================= */
  var header = document.querySelector('[data-header]')
  var mobileBar = window.matchMedia('(max-width: 989px)').matches
  if (header && mobileBar && document.body.classList.contains('header-can-transparent')) {
    var hero = document.querySelector('.hero-tiles')
    if (hero) {
      var setState = function () {
        var past = window.scrollY > hero.offsetHeight - 120
        header.classList.toggle('is-transparent', !past)
      }
      header.classList.add('is-transparent')
      setState()
      window.addEventListener('scroll', setState, { passive: true })
    }
  }

  /* Predictive search
     ======================================================================= */
  var searchInput = document.querySelector('[data-predictive-input]')
  var searchResults = document.querySelector('[data-predictive-results]')
  var searchViewAll = document.querySelector('[data-predictive-viewall]')

  if (searchInput && searchResults) {
    var renderResults = function (products, terms) {
      if (!products.length) {
        searchResults.innerHTML = '<p class="search-overlay__empty">' + escapeHtml(theme.strings.noResults || 'No results found') + '</p>'
        if (searchViewAll) searchViewAll.hidden = true
        return
      }
      var html = products.map(function (p) {
        var imgSrc = p.image ? p.image + (p.image.indexOf('?') > -1 ? '&' : '?') + 'width=360' : ''
        var img = imgSrc ? '<img src="' + imgSrc + '" alt="" loading="lazy">' : ''
        return (
          '<a class="mini-card" href="' + p.url + '">' +
            '<div class="mini-card__media">' + img + '</div>' +
            '<span class="mini-card__title">' + escapeHtml(p.title) + '</span>' +
            '<span class="mini-card__price">' + escapeHtml(p.price || '') + '</span>' +
          '</a>'
        )
      }).join('')
      searchResults.innerHTML = '<div class="pgrid--mini">' + html + '</div>'
      if (searchViewAll) {
        searchViewAll.hidden = false
        searchViewAll.href = theme.routes.search + '?q=' + encodeURIComponent(terms) + '&type=product'
      }
    }

    var doSearch = debounce(function () {
      var terms = searchInput.value.trim()
      if (terms.length < 2) {
        searchResults.innerHTML = ''
        if (searchViewAll) searchViewAll.hidden = true
        return
      }
      var url = theme.routes.predictiveSearch + '.json' +
        '?q=' + encodeURIComponent(terms) +
        '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last'
      fetch(url)
        .then(function (r) { return r.json() })
        .then(function (data) {
          var products = (data.resources && data.resources.results && data.resources.results.products) || []
          renderResults(products.map(function (p) {
            return { title: p.title, url: p.url, image: p.image, price: p.price ? formatMoney(parseFloat(p.price) * 100) : '' }
          }), terms)
        })
        .catch(function () {})
    }, 250)

    searchInput.addEventListener('input', doSearch)
  }

  /* Cart API + drawer refresh
     ======================================================================= */
  function refreshCartDrawer (openAfter) {
    return fetch(theme.routes.root + '?sections=cart-drawer')
      .then(function (r) { return r.json() })
      .then(function (sections) {
        var html = sections['cart-drawer']
        if (!html) return
        var doc = new DOMParser().parseFromString(html, 'text/html')
        var fresh = doc.querySelector('[data-cart-drawer-inner]')
        var current = document.querySelector('#CartDrawer [data-cart-drawer-inner]')
        if (fresh && current) current.innerHTML = fresh.innerHTML
        updateCartCount()
        if (openAfter && theme.cartType === 'drawer') open('cart')
      })
  }

  function updateCartCount () {
    fetch(theme.routes.cart + '.js')
      .then(function (r) { return r.json() })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = '(' + cart.item_count + ')'
        })
      })
      .catch(function () {})
  }

  function showCartError (msg) {
    var el = document.querySelector('[data-cart-error]')
    if (!el) return
    el.textContent = msg || theme.strings.cartError
    el.hidden = false
    setTimeout(function () { el.hidden = true }, 4000)
  }

  function changeLine (line, quantity) {
    fetch(theme.routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart change failed')
        return r.json()
      })
      .then(function () { refreshCartDrawer(false) })
      .catch(function () { showCartError() })
  }

  document.addEventListener('click', function (e) {
    var lineEl = e.target.closest('.cart-line')
    if (!lineEl || !lineEl.closest('#CartDrawer')) return
    var line = parseInt(lineEl.getAttribute('data-line'), 10)
    var input = lineEl.querySelector('[data-qty-input]')
    if (e.target.closest('[data-qty-plus]')) {
      changeLine(line, parseInt(input.value, 10) + 1)
    } else if (e.target.closest('[data-qty-minus]')) {
      changeLine(line, Math.max(0, parseInt(input.value, 10) - 1))
    } else if (e.target.closest('[data-line-remove]')) {
      changeLine(line, 0)
    }
  })

  document.addEventListener('change', function (e) {
    var input = e.target.closest('#CartDrawer [data-qty-input]')
    if (!input) return
    var lineEl = input.closest('.cart-line')
    changeLine(parseInt(lineEl.getAttribute('data-line'), 10), Math.max(0, parseInt(input.value, 10) || 0))
  })

  /* Logo settle: drop the one-shot class once the last letter has landed,
     otherwise the animation's fill would sit on top of the hover transition. */
  if (document.documentElement.classList.contains('logo-drop')) {
    setTimeout(function () { document.documentElement.classList.remove('logo-drop') }, 1900)
  }

  /* Product form: variant state + AJAX add
     ======================================================================= */
  document.querySelectorAll('[data-product-section]').forEach(function (section) {
    var jsonEl = section.querySelector('[data-product-json]')
    if (!jsonEl) return

    var data
    try { data = JSON.parse(jsonEl.textContent) } catch (err) { return }

    var variants = data.variants || []
    var mediaMap = data.mediaMap || {}
    var form = section.querySelector('form[id^="ProductForm"]')
    if (!form) return

    var idInput = form.querySelector('[data-variant-id]')
    var priceWrap = section.querySelector('[data-price-wrap]')
    var stockNote = section.querySelector('[data-stock-note]')
    var addBtn = section.querySelector('[data-add-to-cart]')
    var addLabel = section.querySelector('[data-add-label]')
    var picker = section.querySelector('[data-variant-picker]')
    var picked = false

    function selectedOptions () {
      var opts = []
      if (!picker) return opts
      picker.querySelectorAll('[data-option-index]').forEach(function (fieldset) {
        var checked = fieldset.querySelector('input:checked')
        opts[parseInt(fieldset.getAttribute('data-option-index'), 10)] = checked ? checked.value : null
      })
      return opts
    }

    function findVariant (opts) {
      return variants.find(function (v) {
        return opts.every(function (value, i) {
          return value === null || v['option' + (i + 1)] === value
        })
      })
    }

    function renderPrice (variant) {
      if (!priceWrap || !variant) return
      var html = ''
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html = '<span class="price price--sale"><s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s>' +
          '<span class="price__current">' + formatMoney(variant.price) + '</span></span>'
      } else {
        html = '<span class="price"><span class="price__current">' + formatMoney(variant.price) + '</span></span>'
      }
      priceWrap.innerHTML = html
    }

    function renderStock (variant) {
      if (!stockNote) return
      var threshold = 5
      if (variant && variant.available && typeof variant.inventory_quantity === 'number' &&
          variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold &&
          variant.inventory_management) {
        stockNote.innerHTML = (theme.strings.lowStock || 'Low stock: {{ count }} left').replace('{{ count }}', variant.inventory_quantity)
      } else {
        stockNote.innerHTML = ''
      }
    }

    function markUnavailable (opts) {
      if (!picker) return
      picker.querySelectorAll('[data-option-index]').forEach(function (fieldset) {
        var index = parseInt(fieldset.getAttribute('data-option-index'), 10)
        fieldset.querySelectorAll('.pill').forEach(function (pill) {
          var value = pill.querySelector('input').value
          var test = opts.slice()
          test[index] = value
          var match = variants.find(function (v) {
            return test.every(function (val, i) { return val === null || v['option' + (i + 1)] === val })
          })
          var unavailable = !match || !match.available
          pill.classList.toggle('is-unavailable', unavailable)
          var input = pill.querySelector('input')
          input.disabled = unavailable && !input.checked
        })
      })
    }

    function scrollToMedia (variant) {
      if (!variant || !mediaMap[variant.id]) return
      var target = section.querySelector('[data-media-id="' + mediaMap[variant.id] + '"]')
      if (target && window.matchMedia('(min-width: 990px)').matches) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }

    function update () {
      var opts = selectedOptions()
      var variant = findVariant(opts)
      markUnavailable(opts)
      if (!variant) {
        if (addBtn) { addBtn.disabled = true; addBtn.classList.remove('is-live') }
        if (addLabel) addLabel.textContent = theme.strings.unavailable || 'Unavailable'
        return
      }
      idInput.value = variant.id
      renderPrice(variant)
      renderStock(variant)
      scrollToMedia(variant)
      if (addBtn) {
        addBtn.disabled = !variant.available
        addBtn.classList.toggle('is-live', picked && variant.available)
      }
      if (addLabel) {
        addLabel.textContent = variant.available
          ? (theme.strings.addToCart || 'Add to bag')
          : (theme.strings.soldOut || 'Sold out')
      }
      if (window.history.replaceState) {
        var url = new URL(window.location.href)
        url.searchParams.set('variant', variant.id)
        window.history.replaceState({}, '', url.toString())
      }
    }

    if (picker) {
      picker.addEventListener('change', function (e) {
        if (e.target.matches('[data-option-input]')) { picked = true; update() }
      })
      update()
    }

    form.addEventListener('submit', function (e) {
      if (theme.cartType !== 'drawer') return
      e.preventDefault()
      if (addBtn) {
        addBtn.disabled = true
        if (addLabel) addLabel.textContent = theme.strings.adding || 'Adding…'
      }
      fetch(theme.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: parseInt(idInput.value, 10), quantity: 1 })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (err) { throw err })
          return r.json()
        })
        .then(function () { return refreshCartDrawer(true) })
        .catch(function (err) {
          showCartError(err && err.description)
          if (theme.cartType === 'drawer') open('cart')
        })
        .finally(function () {
          if (addBtn) addBtn.disabled = false
          update()
        })
    })

    /* Mobile gallery counter */
    var gallery = section.querySelector('[data-gallery]')
    var counter = section.querySelector('[data-media-counter]')
    if (gallery && counter) {
      gallery.addEventListener('scroll', debounce(function () {
        var index = Math.round(gallery.scrollLeft / gallery.clientWidth) + 1
        counter.textContent = index
      }, 80), { passive: true })
    }
  })

  /* Product recommendations
     ======================================================================= */
  document.querySelectorAll('[data-recommendations]').forEach(function (el) {
    var url = el.getAttribute('data-url')
    if (!url) return
    fetch(url)
      .then(function (r) { return r.text() })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html')
        var fresh = doc.querySelector('[data-recommendations]')
        if (fresh && fresh.innerHTML.trim().length) el.innerHTML = fresh.innerHTML
      })
      .catch(function () {})
  })

  /* Theme editor support
     ======================================================================= */
  document.addEventListener('shopify:section:select', function (e) {
    var drawer = e.target.querySelector('.drawer')
    if (drawer) open(drawer.getAttribute('data-drawer'))
  })
  document.addEventListener('shopify:section:deselect', close)
})()
